import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AnimatePresence } from "framer-motion";
import { EventsProvider } from "./contexts/EventsContext";
import { LiveStreamProvider } from "./contexts/LiveStreamContext";
import Index from "./pages/Index";
import AdminDashboard from "./pages/AdminDashboard";
import Events from "./pages/admin/Events";
import Partners from "./pages/admin/Partners";
import Users from "./pages/admin/Users";
import Settings from "./pages/admin/Settings";
import Exhibitions from "./pages/admin/Exhibitions";
import Festivals from "./pages/admin/Festivals";
import SocialGroups from "./pages/admin/SocialGroups";
import SectionVisibility from "./pages/admin/SectionVisibility";
import CategoriesManagement from "./pages/admin/CategoriesManagement";
import SaudiFestivals from "./pages/categories/SaudiFestivals";
import SaudiSeasons from "./pages/categories/SaudiSeasons";
import InternationalDays from "./pages/categories/InternationalDays";
import Applications from "./pages/categories/Applications";
import TikTokLives from "./pages/categories/TikTokLives";
import ConferencesSeminars from "./pages/categories/ConferencesSeminars";
import PublicOccasions from "./pages/categories/PublicOccasions";
import XSpaces from "./pages/categories/XSpaces";
import ExhibitionsPage from "./pages/categories/Exhibitions";
import Seasons from "./pages/admin/Seasons";
import Registrations from "./pages/admin/Registrations";
import Analytics from "./pages/admin/Analytics";
import Notifications from "./pages/admin/Notifications";
import Newsletter from "./pages/admin/Newsletter";
import RegistrationNotifications from "./pages/admin/RegistrationNotifications";
import XSpacesAdmin from "./pages/admin/XSpacesAdmin";
import EventsImport from "./pages/admin/EventsImport";
import Submissions from "./pages/admin/Submissions";
import Ads from "./pages/admin/Ads";
import SiteAds from "./pages/admin/SiteAds";
import SectionBannerAdsAdmin from "./pages/admin/SectionBannerAds";
import NewsTickerAdmin from "./pages/admin/NewsTicker";
import ImportedEventsPage from "./pages/ImportedEvents";
import SocialGroupsPage from "./pages/SocialGroups";
import DynamicCategoryPage from "./pages/categories/DynamicCategoryPage";
import Auth from "./pages/Auth";
import Profile from "./pages/Profile";
import Packages from "./pages/Packages";
import PaymentSuccess from "./pages/PaymentSuccess";
import Submit from "./pages/Submit";
import MySubmissions from "./pages/MySubmissions";
import Advertise from "./pages/Advertise";
import AdvertiseSuccess from "./pages/AdvertiseSuccess";
import NotFound from "./pages/NotFound";
import AdminLogin from "./pages/AdminLogin";
import EventDetails from "./pages/EventDetails";
import XSpaceDetails from "./pages/XSpaceDetails";
import SocialGroupDetails from "./pages/SocialGroupDetails";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import AdminProtectedRoute from "./components/auth/AdminProtectedRoute";
import { ScrollToTop } from "./components/ScrollToTop";
import { PageTransition } from "./components/PageTransition";

const queryClient = new QueryClient();

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <EventsProvider>
            <LiveStreamProvider>
              <Toaster />
              <Sonner />
              <Router>
                <AnimatedRoutes />
              </Router>
            </LiveStreamProvider>
          </EventsProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

// Animated Routes component - needs useLocation inside Router
function AnimatedRoutes() {
  const location = useLocation();
  
  return (
    <>
      <ScrollToTop />
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<PageTransition><Index /></PageTransition>} />
          <Route path="/applications" element={<PageTransition><Applications /></PageTransition>} />
          <Route path="/saudi-festivals" element={<PageTransition><SaudiFestivals /></PageTransition>} />
          <Route path="/saudi-seasons" element={<PageTransition><SaudiSeasons /></PageTransition>} />
          <Route path="/international-days" element={<PageTransition><InternationalDays /></PageTransition>} />
          <Route path="/tiktok-lives" element={<PageTransition><TikTokLives /></PageTransition>} />
          <Route path="/conferences-seminars" element={<PageTransition><ConferencesSeminars /></PageTransition>} />
          <Route path="/public-occasions" element={<PageTransition><PublicOccasions /></PageTransition>} />
          <Route path="/x-spaces" element={<PageTransition><XSpaces /></PageTransition>} />
          <Route path="/exhibitions" element={<PageTransition><ExhibitionsPage /></PageTransition>} />
          <Route path="/social-groups" element={<PageTransition><SocialGroupsPage /></PageTransition>} />
          <Route path="/admin-login" element={<PageTransition><AdminLogin /></PageTransition>} />
          <Route path="/admin" element={<Navigate to="/admin-login" replace />} />
          <Route path="/admin-dashboard" element={<AdminProtectedRoute><PageTransition><AdminDashboard /></PageTransition></AdminProtectedRoute>} />
          <Route path="/admin/events" element={<AdminProtectedRoute><PageTransition><Events /></PageTransition></AdminProtectedRoute>} />
          <Route path="/admin/partners" element={<AdminProtectedRoute><PageTransition><Partners /></PageTransition></AdminProtectedRoute>} />
          <Route path="/admin/users" element={<AdminProtectedRoute><PageTransition><Users /></PageTransition></AdminProtectedRoute>} />
          <Route path="/admin/settings" element={<AdminProtectedRoute><PageTransition><Settings /></PageTransition></AdminProtectedRoute>} />
          <Route path="/admin/exhibitions" element={<AdminProtectedRoute><PageTransition><Exhibitions /></PageTransition></AdminProtectedRoute>} />
          <Route path="/admin/festivals" element={<AdminProtectedRoute><PageTransition><Festivals /></PageTransition></AdminProtectedRoute>} />
          <Route path="/admin/seasons" element={<AdminProtectedRoute><PageTransition><Seasons /></PageTransition></AdminProtectedRoute>} />
          <Route path="/admin/registrations" element={<AdminProtectedRoute><PageTransition><Registrations /></PageTransition></AdminProtectedRoute>} />
          <Route path="/admin/analytics" element={<AdminProtectedRoute><PageTransition><Analytics /></PageTransition></AdminProtectedRoute>} />
          <Route path="/admin/notifications" element={<AdminProtectedRoute><PageTransition><Notifications /></PageTransition></AdminProtectedRoute>} />
          <Route path="/admin/newsletter" element={<AdminProtectedRoute><PageTransition><Newsletter /></PageTransition></AdminProtectedRoute>} />
          <Route path="/admin/social-groups" element={<AdminProtectedRoute><PageTransition><SocialGroups /></PageTransition></AdminProtectedRoute>} />
          <Route path="/admin/section-visibility" element={<AdminProtectedRoute><PageTransition><SectionVisibility /></PageTransition></AdminProtectedRoute>} />
          <Route path="/admin/categories" element={<AdminProtectedRoute><PageTransition><CategoriesManagement /></PageTransition></AdminProtectedRoute>} />
          <Route path="/admin/x-spaces" element={<AdminProtectedRoute><PageTransition><XSpacesAdmin /></PageTransition></AdminProtectedRoute>} />
          <Route path="/admin/events-import" element={<AdminProtectedRoute><PageTransition><EventsImport /></PageTransition></AdminProtectedRoute>} />
          <Route path="/admin/registration-notifications" element={<AdminProtectedRoute><PageTransition><RegistrationNotifications /></PageTransition></AdminProtectedRoute>} />
          <Route path="/admin/submissions" element={<AdminProtectedRoute><PageTransition><Submissions /></PageTransition></AdminProtectedRoute>} />
          <Route path="/admin/news-ticker" element={<AdminProtectedRoute><PageTransition><NewsTickerAdmin /></PageTransition></AdminProtectedRoute>} />
          <Route path="/admin/ads" element={<AdminProtectedRoute><PageTransition><Ads /></PageTransition></AdminProtectedRoute>} />
          <Route path="/admin/site-ads" element={<AdminProtectedRoute><PageTransition><SiteAds /></PageTransition></AdminProtectedRoute>} />
          <Route path="/admin/section-banner-ads" element={<AdminProtectedRoute><PageTransition><SectionBannerAdsAdmin /></PageTransition></AdminProtectedRoute>} />
          <Route path="/auth" element={<PageTransition><Auth /></PageTransition>} />
          <Route path="/profile" element={<PageTransition><Profile /></PageTransition>} />
          <Route path="/packages" element={<PageTransition><Packages /></PageTransition>} />
          <Route path="/payment-success" element={<PageTransition><PaymentSuccess /></PageTransition>} />
          <Route path="/submit" element={<PageTransition><Submit /></PageTransition>} />
          <Route path="/my-submissions" element={<PageTransition><MySubmissions /></PageTransition>} />
          <Route path="/advertise" element={<PageTransition><Advertise /></PageTransition>} />
          <Route path="/advertise/success" element={<PageTransition><AdvertiseSuccess /></PageTransition>} />
          <Route path="/imported-events" element={<PageTransition><ImportedEventsPage /></PageTransition>} />
          <Route path="/event/:id" element={<PageTransition><EventDetails /></PageTransition>} />
          <Route path="/x-space/:id" element={<PageTransition><XSpaceDetails /></PageTransition>} />
          <Route path="/social-group/:id" element={<PageTransition><SocialGroupDetails /></PageTransition>} />
          {/* Dynamic category route - catches all custom categories */}
          <Route path="/category/:slug" element={<PageTransition><DynamicCategoryWrapper /></PageTransition>} />
          <Route path="/privacy-policy" element={<PageTransition><PrivacyPolicy /></PageTransition>} />
          <Route path="/terms-of-service" element={<PageTransition><TermsOfService /></PageTransition>} />
          <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
        </Routes>
      </AnimatePresence>
    </>
  );
}

// Wrapper component for dynamic categories
function DynamicCategoryWrapper() {
  const { slug } = useParams();
  return <DynamicCategoryPage categoryPath={`/${slug}`} />;
}

export default App;
